import type React from 'react'
import { type PropsWithChildren } from 'react'
import { useIntl } from 'react-intl'

import { useNavigate } from '@tanstack/react-router'

import { AdminHeader } from '@/components/organisms/AdminHeader'
import { MessagingRail, type MessagingRailProps } from '@/components/organisms/MessagingRail'
import { useNotificationSoundControl } from '@/model/contexts/useNotificationSoundControl'

export interface MessagingShellProps {
  rail: MessagingRailProps
  regionName: string
  piloteRegionId: number
  readOnly?: boolean
  readOnlyDate?: string
}

export const MessagingShell: React.FC<PropsWithChildren<MessagingShellProps>> = ({
  rail,
  regionName,
  piloteRegionId,
  readOnly,
  readOnlyDate,
  children,
}) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const { soundEnabled, toggleSound } = useNotificationSoundControl()

  return (
    <div className="flex h-screen flex-col bg-gray-800">
      <AdminHeader
        className="pr-10 pl-[26px]"
        title={
          readOnly
            ? intl.formatMessage({ id: 'message.page.title.readonly' }, { date: readOnlyDate ?? '' })
            : intl.formatMessage({ id: 'message.page.title' })
        }
        titleClassName="text-teal-200"
        backToPilote="button"
        onBackToPiloteClicked={
          readOnly
            ? undefined
            : async () => {
                await navigate({ to: '/', search: { central_id: piloteRegionId } })
              }
        }
        regionName={regionName}
        notificationSoundState={readOnly ? undefined : soundEnabled ? 'enabled' : 'muted'}
        onNotificationSoundToggled={readOnly ? undefined : toggleSound}
      />

      <div className="flex h-[calc(100vh-6rem)] gap-1 overflow-hidden px-4">
        <MessagingRail {...rail} />
        {children}
      </div>
    </div>
  )
}
